import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  Upload,
} from 'lucide-react';
import {
  aiPurchasesAPI,
  bankingAPI,
  purchasePaymentsAPI,
  reconciliationAPI,
} from '../../api';
import { AIDraftReviewPanel } from './AIDraftReviewPanel';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import { useToast } from '../../hooks/useToast';

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'];

function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

function validateFile(file) {
  const fileExt = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExt)) {
    return { valid: false, error: 'Only PDF and image files are allowed' };
  }
  if (file.size > 10 * 1024 * 1024) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }
  return { valid: true };
}

function defaultPaymentAmount(line, purchase) {
  const bankAbs = Math.abs(Number(line?.amount ?? 0));
  const balanceDue = Number(purchase?.balance_due ?? purchase?.total ?? 0);
  if (!Number.isFinite(bankAbs) || bankAbs <= 0) {
    return Number.isFinite(balanceDue) ? balanceDue.toFixed(2) : '';
  }
  if (!Number.isFinite(balanceDue) || balanceDue <= 0) {
    return bankAbs.toFixed(2);
  }
  return Math.min(bankAbs, balanceDue).toFixed(2);
}

/**
 * Inline receipt upload wizard for the reconciliation workbench:
 * upload → AI review → purchase payment → reconciliation match.
 */
export function StatementReceiptUploadModal({
  open,
  onOpenChange,
  line,
  bankAccountId,
  onComplete,
}) {
  const { success, error, ToastContainer } = useToast();
  const fileInputRef = useRef(null);
  const pickerTriggeredRef = useRef(false);

  const [step, setStep] = useState('upload');
  const [uploadError, setUploadError] = useState('');
  const [draft, setDraft] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [bankAccount, setBankAccount] = useState(null);

  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [reconcileNow, setReconcileNow] = useState(true);
  const [paymentError, setPaymentError] = useState('');
  const [paymentBusy, setPaymentBusy] = useState(false);

  const resetState = useCallback(() => {
    setStep('upload');
    setUploadError('');
    setDraft(null);
    setPurchase(null);
    setBankAccount(null);
    setApproving(false);
    setRejecting(false);
    setPaymentAmount('');
    setPaymentDate('');
    setPaymentReference('');
    setReconcileNow(true);
    setPaymentError('');
    setPaymentBusy(false);
    pickerTriggeredRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line?.id]);

  useEffect(() => {
    if (!open || !line || step !== 'upload' || pickerTriggeredRef.current) return;
    pickerTriggeredRef.current = true;
    const timer = setTimeout(() => fileInputRef.current?.click(), 150);
    return () => clearTimeout(timer);
  }, [open, line, step]);

  const balanceDue = useMemo(
    () => Number(purchase?.balance_due ?? purchase?.total ?? 0),
    [purchase]
  );

  const canConfirmPayment = useMemo(() => {
    const amt = Number(paymentAmount);
    return (
      !!purchase?.id &&
      !!bankAccountId &&
      !!line?.id &&
      Number.isFinite(amt) &&
      amt > 0 &&
      amt <= balanceDue
    );
  }, [purchase?.id, bankAccountId, line?.id, paymentAmount, balanceDue]);

  const processFile = async (file) => {
    if (!file || !line?.id || !bankAccountId) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }

    setUploadError('');
    setStep('processing');

    try {
      const result = await aiPurchasesAPI.upload(file, {
        statementLineId: line.id,
        bankAccountId,
      });
      setDraft(result.draft);
      setStep('review');
    } catch (err) {
      if (err.response?.status === 403) {
        const msg =
          'AI purchase processing is not enabled for your account. Please contact your administrator.';
        setUploadError(msg);
        error(msg);
      } else {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          err.message ||
          'Upload failed';
        setUploadError(msg);
        error(msg);
      }
      setStep('upload');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (step === 'processing') return;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleApprove = async (draftId) => {
    setApproving(true);
    try {
      const result = await aiPurchasesAPI.approve(draftId);
      const approvedPurchase = result.purchase;
      setPurchase(approvedPurchase);
      setPaymentAmount(defaultPaymentAmount(line, approvedPurchase));
      setPaymentDate(
        line?.transaction_date ? String(line.transaction_date).slice(0, 10) : ''
      );
      setPaymentReference(line?.reference || '');

      try {
        const account = await bankingAPI.getAccount(bankAccountId);
        setBankAccount(account);
      } catch {
        setBankAccount(null);
      }

      setStep('payment');
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to approve draft');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (draftId) => {
    setRejecting(true);
    try {
      await aiPurchasesAPI.reject(draftId, '');
      success('Draft rejected');
      onOpenChange(false);
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to reject draft');
    } finally {
      setRejecting(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!canConfirmPayment) return;

    const amt = Number(paymentAmount);
    if (amt > balanceDue) {
      setPaymentError(
        `Payment amount cannot exceed balance due of ${formatMoney(balanceDue)}`
      );
      return;
    }

    setPaymentError('');
    setPaymentBusy(true);

    try {
      const paymentResult = await purchasePaymentsAPI.create({
        purchase_id: purchase.id,
        bank_account_id: bankAccountId,
        amount: amt.toString(),
        payment_date: paymentDate || undefined,
        reference: paymentReference?.trim() || undefined,
        payment_method: 'manual',
      });

      const paymentId = paymentResult.payment?.id ?? paymentResult.id;

      const created = await reconciliationAPI.createMatch({
        bank_account_id: bankAccountId,
        match_type: 'purchase_payment',
        match_strategy: 'manual',
        total_amount: amt,
        match_date: paymentDate || undefined,
        reference: paymentReference?.trim() || null,
        allocations: [
          {
            statement_line_id: line.id,
            allocated_amount: amt,
            target_type: 'purchase_payment',
            target_id: paymentId,
          },
        ],
      });

      if (reconcileNow) {
        await reconciliationAPI.reconcileMatch(created.match.id);
      }

      success(
        reconcileNow
          ? 'Receipt processed, payment recorded, and line reconciled.'
          : 'Receipt processed and draft match created.'
      );
      onComplete?.(created.match);
      onOpenChange(false);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.errors?.[0]?.msg ||
        err.message ||
        'Could not record payment or create match';
      setPaymentError(msg);
    } finally {
      setPaymentBusy(false);
    }
  };

  const titleByStep = {
    upload: 'Upload receipt',
    processing: 'Processing receipt…',
    review: 'Review extracted data',
    payment: 'Record payment & match',
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title={titleByStep[step] || 'Upload receipt'}
      width="w-full max-w-3xl"
    >
      {!line ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No statement line selected.</p>
      ) : (
        <div className="space-y-4">
          {(step === 'upload' || step === 'processing') && (
            <>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-[var(--ds-radius-md)] p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    AI extraction is experimental — verify supplier, amounts, and line items before
                    approving.
                  </p>
                </div>
              </div>

              <div
                onDragOver={step === 'processing' ? undefined : handleDragOver}
                onDrop={step === 'processing' ? undefined : handleDrop}
                onClick={step === 'processing' ? undefined : () => fileInputRef.current?.click()}
                className={`border-2 border-dashed border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-10 text-center transition-colors ${
                  step === 'processing'
                    ? 'border-[var(--ds-accent-primary)] opacity-75 cursor-not-allowed'
                    : 'hover:border-[var(--ds-accent-primary)] cursor-pointer'
                }`}
              >
                {step === 'processing' ? (
                  <>
                    <Loader2 className="w-10 h-10 mx-auto mb-3 text-[var(--ds-accent-primary)] animate-spin" />
                    <h3 className="text-h4 font-medium text-[var(--ds-text-primary)] mb-1">
                      Processing receipt…
                    </h3>
                    <p className="text-xs text-[var(--ds-text-secondary)]">
                      This may take 30 seconds or more — please keep this panel open
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto mb-3 text-[var(--ds-text-secondary)]" />
                    <h3 className="text-h4 font-medium text-[var(--ds-text-primary)] mb-1">
                      Drop supplier receipt here
                    </h3>
                    <p className="text-xs text-[var(--ds-text-secondary)] mb-3">
                      or click to browse
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Select file
                    </Button>
                    <p className="text-meta text-[var(--ds-text-secondary)] mt-3">
                      PDF, JPG, PNG, GIF, WEBP — max 10MB
                    </p>
                  </>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,image/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={step === 'processing'}
              />

              {uploadError && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{uploadError}</span>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {step === 'review' && draft && (
            <AIDraftReviewPanel
              draft={draft}
              statementLine={line}
              onApprove={handleApprove}
              onReject={handleReject}
              onDraftUpdated={setDraft}
              approving={approving}
              rejecting={rejecting}
              toast={{ success, error }}
            />
          )}

          {step === 'payment' && purchase && (
            <>
              <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
                <CardContent className="flex gap-2 p-3 text-xs text-green-800 dark:text-green-200">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Purchase created</p>
                    <p className="mt-1">
                      {purchase.purchase_num || 'Purchase'} — balance due{' '}
                      {formatMoney(balanceDue)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {paymentError && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{paymentError}</span>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <FormLabel required>Payment amount</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={balanceDue}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Maximum: {formatMoney(balanceDue)}
                  </p>
                </div>
                <div>
                  <FormLabel>Payment date</FormLabel>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="Payment reference"
                    className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <FormLabel>Bank account</FormLabel>
                  <div className="flex h-9 items-center rounded-md border border-gray-300 bg-gray-50 px-3 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    <FileText className="mr-2 h-3.5 w-3.5 shrink-0 opacity-60" />
                    {bankAccount?.account_name || 'Current reconciliation account'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input
                  id="receipt-reconcile-now"
                  type="checkbox"
                  checked={reconcileNow}
                  onChange={(e) => setReconcileNow(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                <label htmlFor="receipt-reconcile-now">Reconcile now</label>
                <span className="text-gray-500 dark:text-gray-400">
                  (uncheck to save as draft match)
                </span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={paymentBusy}
                >
                  Cancel
                </Button>
                <Button onClick={handleConfirmPayment} disabled={!canConfirmPayment || paymentBusy}>
                  {paymentBusy
                    ? 'Saving…'
                    : reconcileNow
                      ? 'Pay & reconcile'
                      : 'Pay & create draft match'}
                </Button>
              </div>
            </>
          )}

          <ToastContainer />
        </div>
      )}
    </SlideOver>
  );
}

export default StatementReceiptUploadModal;
